import { defineStore } from "pinia";
import { AuthAPI } from "@/util/generated/api-auth";
import dayjs from "../plugins/dayjs";
import { useContextStore } from "./contextStore";
import { useNotificationStore } from "./notificationStore";
import { useSettingsStore } from "./settingsStore";
import localforage from "localforage";
import { ofetch } from "ofetch";

export const useAuthStore = defineStore("auth", () => {
  const cacheStorage = localforage.createInstance({
    name: "authStore",
  });
  cacheStorage.setDriver(localforage.LOCALSTORAGE);
  const moduleStore = useModuleStore();
  const contextStore = useContextStore();
  const stepsStore = useStepsStore();
  const notificationStore = useNotificationStore();
  const settingsStore = useSettingsStore();
  const auth = ref(null);
  const me = AuthAPI.getCurrentUser.cache().ref(null);
  const developerMode = ref(
    !import.meta.env.VITE_VUE_APP_API_BASE_PATH ||
      (localStorage.getItem("baseUrl") && localStorage.getItem("baseUrl") !== import.meta.env.VITE_VUE_APP_API_BASE_PATH)
  );

  const isAuthenticated = computed(() => !!auth.value);

  async function authenticate(payload = undefined) {
    let new_auth = payload;
    if (typeof payload === "undefined") {
      if (auth.value) {
        new_auth = auth.value;
      } else {
        new_auth = JSON.parse("" + localStorage.getItem("auth"));
      }
    }
    if (!new_auth || new_auth.auth == false) {
      return null;
    }
    /**
     * Calculate expires_date if not set
     */
    if (!new_auth.expires_in) {
      new_auth.expires_in = 6 * 30 * 24 * 3600;
      new_auth.expires_date = null;
    }
    if (!new_auth.expires_date) {
      new_auth.expires_date = new Date().getTime() + new_auth.expires_in * 1000;
    }
    if (new_auth.expires_date < new Date().getTime() + 30000) {
      console.error("JWT token is expired", new_auth.expires_in, new_auth.expires_date);
      return logout();
    }
    /**
     * Don't load me from server if already loaded and auth still valid
     */
    if (auth.value === new_auth && me.value) {
      return me.value;
    }
    /**
     * Load me from server
     */
    auth.value = new_auth;
    if (new_auth) {
      localStorage.setItem("auth", JSON.stringify(new_auth));
    } else {
      localStorage.removeItem("auth");
    }
    await reloadUser();
    return me.value;
  }

  function setMe(newMe) {
    if (newMe && !newMe.event_event) {
      console.error("Wrong ME data", newMe);
      useRouter()
        .push("/logout")
        .catch(() => {});
      return;
    }
    me.value = newMe;
    if (newMe) {
      dayjs.setTimezone(newMe.event_event.timezone);
      contextStore.setEvent(newMe.event_event);
    }
    localStorage.setItem("me", JSON.stringify(newMe));

    if (window["Sentry"] && window["Sentry"].setUser) {
      if (newMe) {
        window["Sentry"].setUser({
          id: newMe.registration_number || newMe.id,
          email: newMe.email,
          username: newMe.phone_number,
        });
      } else {
        window["Sentry"].configureScope((scope) => scope.setUser(null));
      }
    }
    if (window["_paq"] && newMe) {
      window["_paq"].push(["setUserId", newMe.registration_number || newMe.id]);
      window["_paq"].push(["setCustomDimension", "2", newMe.event_event.name]);
      if (newMe.organization_organization) {
        window["_paq"].push(["setCustomDimension", "3", newMe.organization_organization.name]);
      }
    }
  }

  async function enableDeveloperMode() {
    developerMode.value = true;
  }

  /**
   * @param {boolean} detachPushIdentity - true only when the participant deliberately leaves the device.
   */
  async function logout(detachPushIdentity = false) {
    if (moduleStore.isPedometerEnabled) {
      try {
        await stepsStore.disable();
      } catch (error) {
        console.error("Error disabling stepper:", error);
      }
    }

    // OneSignal.logout() moves the push subscription onto a brand new anonymous user, with no external id and no
    // tags. Most calls here are not a departure at all — a 401, an expired JWT, an env switch — and detaching on
    // those is what produced the crowd of blank OneSignal users. Only detach when the device is actually handed
    // back, and rely on the login() replayed at every authenticated start for everything else.
    if (detachPushIdentity) {
      try {
        await notificationStore.logout();
      } catch (error) {
        console.error("Error logging out from onesignal:", error);
      }
    }

    for (let key of Object.keys(localStorage)) {
      if (["locale", "baseUrl", "likes"].indexOf(key) === -1) {
        localStorage.removeItem(key);
      }
    }
  }

  async function reloadUser(force = false) {
    let meData;
    if ((await cacheStorage.getItem("me")) && !auth.value?.shadow && !force) {
      meData = await cacheStorage.getItem("me");
    } else {
      meData = await ofetch("/n/api/auth/me", {
        baseURL:
          localStorage.getItem("baseUrl") ||
          import.meta.env.VITE_VUE_APP_API_BASE_PATH ||
          (window.cordova?.platformId ? "https://go.preprod.alvarum.com/" : window.location.origin + "/"),
        retryDelay: 600,
        retry: 5,
        headers: {
          Sid: settingsStore.sid,
          "X-App-Version": import.meta.env.VITE_VUE_APP_RELEASE || "alvarum-go-" + (window.cordova?.platformId || "web") + "@dev",
          ...(auth.value && { Authorization: `Bearer ${auth.value.access_token}` }),
          ...(window.device && { "X-Device": `${window.device.version}:${window.device.manufacturer} ${window.device.model}` }),
        },
        query: auth.value?.shadow ? { shadow: true } : undefined,
        responseType: "json",
      });

      await cacheStorage.setItem("me", meData);
    }

    if (!meData) {
      console.error("JWT token may be invalid or expired");
      await logout();
      setTimeout(function () {
        if (window["cordova"]?.platformId) {
          window.location.href = window.location.pathname;
        } else {
          window.location.href = "/";
        }
      }, 1000);
      return;
    }
    setMe(meData);
  }

  return {
    auth,
    me,
    isAuthenticated,
    developerMode,
    setMe,
    enableDeveloperMode,
    logout,
    reloadUser,
    authenticate,
  };
});
